/**
 * In-app PDF preview using pdf.js (pdfjs-dist).
 *
 * Renders an uploaded PDF inline to canvas WITHOUT executing any JavaScript the
 * PDF may embed: pdf.js only runs PDF scripting when the scripting API is
 * explicitly enabled (we never do), and `isEvalSupported: false` disables eval.
 * This is why the backend can keep serving PDFs as a download for direct
 * navigation (defence-in-depth against the browser's native viewer, CWE-79)
 * while still giving a true inline preview here — a `fetch()` reads the bytes
 * regardless of the Content-Disposition, and pdf.js renders them safely.
 *
 * @link useDocuments.handlePreview for the caller that opens this for PDFs.
 */
import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

// Worker is copied to /public so it's served from the app's own origin
// (CSP-safe, no CDN). See the postinstall copy in the docs feature.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Short-lived signed URL that streams the PDF bytes. */
  fileUrl: string | null;
  title?: string;
  /** Fallback download (opens the signed URL directly) when render fails. */
  onDownload?: () => void;
}

/**
 * Modal that renders a PDF inline via pdf.js. Fetches the bytes from the signed
 * URL, then draws each page to a canvas. Embedded PDF JavaScript never runs.
 */
export default function PdfPreviewModal({ isOpen, onClose, fileUrl, title, onDownload }: PdfPreviewModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !fileUrl) return;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pdf.js loading task type
    let loadingTask: { destroy: () => void; promise: Promise<any> } | null = null;

    const render = async () => {
      setLoading(true);
      setError(null);
      const container = containerRef.current;
      if (container) container.innerHTML = '';
      try {
        const resp = await fetch(fileUrl, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.arrayBuffer();
        if (cancelled) return;
        // isEvalSupported:false disables eval; scripting stays off (default),
        // so any JavaScript embedded in the PDF is never executed.
        loadingTask = pdfjsLib.getDocument({ data, isEvalSupported: false });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const target = containerRef.current;
        if (!target) return;
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement('canvas');
          canvas.className = 'mx-auto mb-4 shadow rounded max-w-full h-auto';
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          target.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
        if (!cancelled) setLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('PDF preview failed:', err);
        setError('This PDF could not be previewed. You can download it instead.');
        setLoading(false);
      }
    };

    render();
    return () => {
      cancelled = true;
      try { loadingTask?.destroy(); } catch { /* already gone */ }
    };
  }, [isOpen, fileUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{title || 'Preview'}</DialogTitle>
        </DialogHeader>
        <div className="relative flex-1 overflow-y-auto bg-muted/40 rounded-md p-4 min-h-[300px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="text-sm text-muted-foreground">{error}</p>
              {onDownload && (
                <Button variant="outline" size="sm" onClick={onDownload}>
                  <Download className="h-4 w-4 mr-2" /> Download
                </Button>
              )}
            </div>
          )}
          <div ref={containerRef} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
